import classNames from 'classnames';
import { useController, useFormContext } from 'react-hook-form';
import { AutoComplete } from '../../../components/AutoComplete/AutoComplete.js';
import { fieldCantBeEmptyMsg, noSelectionMsg, selectMsg, t } from '../../i18n/index.js';
import { getTextError } from '../utils.js';
import { CREATE_NEW_CONTACT_OPTION_VALUE, getContactOptions } from './ContactDropdown.js';
import type { BasicAutoCompleteItem } from '../../../components/AutoComplete/AutoComplete.js';
import type { Contact as ContactInterface } from '../../../generated/api/contact.js';

interface ContactProps {
  className?: string;
  contacts: Array<ContactInterface>;
  name: string;
  reference: string;
  enableAddNewContact?: boolean;
  labelText?: string;
  onCreateContact?: () => void;
  disabled?: boolean;
}

export const CREATED_CONTACT = 'CREATED_CONTACT';

export const Contact = (props: ContactProps) => {
  const { name, reference, contacts, enableAddNewContact, className, onCreateContact, labelText, disabled } = props;
  const methods = useFormContext();
  const values = methods.watch();
  const reset = values.key || 0;
  const newContact = values[reference]?.contactId === CREATED_CONTACT ? [values[reference]] : [];
  const contactOptions = getContactOptions([...newContact, ...contacts], enableAddNewContact !== false);
  const { field, fieldState, formState } = useController({
    name,
    control: methods.control,
    rules: { required: { value: true, message: t.VPVR(fieldCantBeEmptyMsg) } },
  });
  const value = name.split('.').reduce((a, c) => (a ? a[c] : undefined), formState.defaultValues);
  const defaultValue = contactOptions.find(i => (reset === 0 ? i.value === value : i.value === field.value));
  const error = getTextError(formState.isSubmitted, fieldState.isTouched, fieldState.error);

  return (
    <AutoComplete<BasicAutoCompleteItem>
      className={classNames('large-min-height', className)}
      inputRef={field.ref}
      options={contactOptions}
      defaultOption={defaultValue}
      getUniqueId={i => i.value}
      getDisplayHtml={i => i.html}
      getDisplayValue={i => i.label}
      onInputBlur={field.onBlur}
      onInputChange={(e, item) => {
        if (item.value === CREATE_NEW_CONTACT_OPTION_VALUE && onCreateContact) {
          onCreateContact();
          return;
        }
        methods.setValue(name, item.value);
        methods.setError(name, { message: undefined });
      }}
      id={`${name}-autocomplete`}
      noOptionsMsg={t.ZW5W(noSelectionMsg)}
      label={labelText ?? t.VYZS('Contact person')}
      placeholder={t.QRYV(selectMsg)}
      error={error}
      config={{ isSortable: false }}
      // If key changes = AutoComplete resets (unmount and mount)
      // This is the correct way to update value from the parent component
      key={`${name}-autocomplete${reset}`}
      disabled={disabled}
    />
  );
};
